import { Preferences } from '@capacitor/preferences';

export type SerializableHistoryEntry = [string, string];

export interface HistoryStorageState {
  history: SerializableHistoryEntry[];
  currentIndex: number;
}

interface PersistedHistoryStorageState extends HistoryStorageState {
  version: number;
  lastAccessedAt: number;
}

const HISTORY_STORAGE_VERSION = 1;
const HISTORY_STORAGE_KEY_PREFIX = 'systemHistory';
const MAX_HISTORY_LENGTH = 100;

// TODO: Security: We intentionally persist local route history to support
// resume-on-relogin. This exposes route metadata, such as entity IDs and query
// params, to anyone with local device/storage access. Revisit when time allows;
// likely mitigation is an encrypted/minimized payload backed by an appropriate
// client-side key or secure storage strategy.

function hashKeyPart(value: string): string {
  let hash = 0;

  for (let index = 0; index < value.length; index++) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }

  return Math.abs(hash).toString(36);
}

export function buildSystemHistoryStorageKey(
  userId: string | undefined,
  endpointTokenValue: unknown,
): string | null {
  if (!userId) {
    return null;
  }

  const normalizedEndpointTokenValue =
    endpointTokenValue === undefined || endpointTokenValue === null
      ? 'default'
      : String(endpointTokenValue);

  return [
    HISTORY_STORAGE_KEY_PREFIX,
    `v${HISTORY_STORAGE_VERSION}`,
    encodeURIComponent(userId),
    hashKeyPart(normalizedEndpointTokenValue),
  ].join(':');
}

export function normalizeHistoryStorageState(
  state: HistoryStorageState,
): HistoryStorageState {
  const validHistory = state.history.filter(
    (entry): entry is SerializableHistoryEntry =>
      Array.isArray(entry) &&
      entry.length === 2 &&
      typeof entry[0] === 'string' &&
      typeof entry[1] === 'string',
  );
  const entriesToRemove = Math.max(0, validHistory.length - MAX_HISTORY_LENGTH);
  const history = validHistory.slice(entriesToRemove);
  const adjustedIndex = Math.max(0, state.currentIndex - entriesToRemove);
  const currentIndex = history.length
    ? Math.min(history.length - 1, adjustedIndex)
    : 0;

  return {
    history,
    currentIndex,
  };
}

export function parseStoredHistoryState(
  rawValue: string | null | undefined,
): HistoryStorageState | null {
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(
      rawValue,
    ) as Partial<PersistedHistoryStorageState>;

    if (!Array.isArray(parsed.history)) {
      return null;
    }

    return normalizeHistoryStorageState({
      history: parsed.history as SerializableHistoryEntry[],
      currentIndex:
        typeof parsed.currentIndex === 'number' ? parsed.currentIndex : 0,
    });
  } catch {
    return null;
  }
}

export async function loadHistoryStorageState(
  key: string,
): Promise<HistoryStorageState | null> {
  const { value } = await Preferences.get({ key });
  return parseStoredHistoryState(value);
}

export async function saveHistoryStorageState(
  key: string,
  state: HistoryStorageState,
): Promise<void> {
  const normalizedState = normalizeHistoryStorageState(state);
  const persistedState: PersistedHistoryStorageState = {
    ...normalizedState,
    version: HISTORY_STORAGE_VERSION,
    lastAccessedAt: Date.now(),
  };

  await Preferences.set({
    key,
    value: JSON.stringify(persistedState),
  });
}

export async function removeHistoryStorageState(key: string): Promise<void> {
  await Preferences.remove({ key });
}
